import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { PublicBrand } from '@aralere/shared-types';
import { brandDisplayName } from './util/text.js';

interface BrandContextValue {
  brand: PublicBrand | null;
  loading: boolean;
  error: Error | null;
}

const BrandContext = createContext<BrandContextValue>({
  brand: null,
  loading: true,
  error: null,
});

export function useBrand(): BrandContextValue {
  return useContext(BrandContext);
}

interface BrandProviderProps {
  children: ReactNode;
  /** Optional fallback brand to render before the API responds (for first paint). */
  fallback?: PublicBrand;
}

export function BrandProvider({ children, fallback }: BrandProviderProps): JSX.Element {
  const [brand, setBrand] = useState<PublicBrand | null>(fallback ?? null);
  const [loading, setLoading] = useState(!fallback);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/brand')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Brand fetch failed: ${r.status}`))))
      .then((data: { brand: PublicBrand }) => {
        if (cancelled) return;
        setBrand(data.brand);
        applyBrandToDocument(data.brand);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <BrandContext.Provider value={{ brand, loading, error }}>
      {children}
    </BrandContext.Provider>
  );
}

function applyBrandToDocument(brand: PublicBrand): void {
  const root = document.documentElement;
  // 04 #95 brand seam: primary + accent are theme-independent and stay on their
  // themed names directly (no theme ever re-points them). The three SURFACE colors
  // are written to the --brand-* names instead: the theme sheet (global.css) maps
  // --color-background/-text/-muted FROM them in light and re-points them in dark.
  // Writing the themed names inline here would defeat the stylesheet dark override,
  // because inline styles outrank any selector.
  root.style.setProperty('--color-primary', brand.branding.colors.primary);
  root.style.setProperty('--color-accent', brand.branding.colors.accent);
  root.style.setProperty('--brand-background', brand.branding.colors.background);
  const bgRgb = hexToRgbChannels(brand.branding.colors.background);
  if (bgRgb) root.style.setProperty('--brand-background-rgb', bgRgb);
  root.style.setProperty('--brand-text', brand.branding.colors.text);
  root.style.setProperty('--brand-muted', brand.branding.colors.muted);
  root.style.setProperty('--font-display', `'${brand.branding.fonts.display}', sans-serif`);
  root.style.setProperty('--font-body', `'${brand.branding.fonts.body}', sans-serif`);
  document.title = brandDisplayName(brand.name);
  const favicon = document.querySelector("link[rel='icon']");
  if (favicon && brand.branding.faviconUrl) {
    favicon.setAttribute('href', brand.branding.faviconUrl);
  }
  injectBrandFonts(brand);
}

/**
 * Parse a hex colour (#rgb or #rrggbb) into space-separated channels "r g b" for use
 * in `rgb(... / <alpha>)`. Lets the tokenized glass header tint the brand background at
 * a variable alpha (so "off" can render a solid header). Returns null for non-hex input,
 * leaving the index.html first-paint fallback in place.
 */
function hexToRgbChannels(hex: string): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

/**
 * Inject the brand's Google Fonts stylesheet at runtime. The single multi-brand app
 * doesn't bake any one brand's fonts into index.html; instead we load exactly the
 * fonts this brand needs, keyed off the brand record. Scales to any number of brands.
 */
function injectBrandFonts(brand: PublicBrand): void {
  const families = [brand.branding.fonts.display, brand.branding.fonts.body]
    .filter((f, i, arr) => f && arr.indexOf(f) === i); // unique, truthy
  if (families.length === 0) return;

  // Build a Google Fonts URL with a sensible weight range for each family.
  const familyParams = families
    .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, '+')}:wght@400;500;600;700`)
    .join('&');
  const href = `https://fonts.googleapis.com/css2?${familyParams}&display=swap`;

  // Avoid injecting duplicates if the brand re-applies.
  const existing = document.querySelector('link[data-brand-fonts]');
  if (existing) {
    if (existing.getAttribute('href') === href) return;
    existing.remove();
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.setAttribute('data-brand-fonts', 'true');
  document.head.appendChild(link);
}
